import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Card, CardContent, CircularProgress, Typography, useTheme } from '@mui/material'
import { useDispatch } from 'react-redux'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IExecutionEnvironment } from '../../../definitions/NodesExecution'
import Formatter from '../../../helpers/Formatter'
import { changeRoute } from '../../../store/slices/SessionSlice'
import useCommand from '../../hooks/useCommand'

interface P {
    execEnv: IExecutionEnvironment
    timer: number
}

const ModalEndExecutionEnvironment = ({ execEnv, timer }: P) => {
    const theme = useTheme()
    const dispatch = useDispatch()

    const { status, execute } = useCommand({
        commandFn: async () => await FlowApiClient.getSettlement(execEnv?.uuid),
        onFinish: response => {
            dispatch(
                changeRoute({
                    route: 'settlement-details',
                    params: {
                        settlementId: response.response.uuid,
                        contractId: response.response.contractId,
                        eeId: response.response.executionEnvironmentId
                    }
                })
            )
        }
    })

    const renderContent = () => {
        if (execEnv?.status === 'completed') {
            if (execEnv?.flow?.nodes?.find(n => n.type === 'stmt_settlement')) {
                return completeSettlement()
            } else {
                return completeGeneric()
            }
        } else if (execEnv?.status === 'failed') {
            if (execEnv?.flow?.nodes?.find(n => n.type === 'stmt_settlement')) {
                return failedSettlement()
            } else {
                return failedGeneric()
            }
        }
    }

    const completeSettlement = () => {
        return (
            <>
                <Box width='100%' display='flex' justifyContent='center'>
                    <Box height={60} width={60} border='2px solid' borderColor={theme.palette.success.main} borderRadius={999} display='flex' justifyContent='center' alignItems='center'>
                        <CheckIcon fontSize='large' color='success' />
                    </Box>
                </Box>
                <Typography variant='h5' textAlign='center' fontWeight='500' marginTop={3}>Settlement Completed Successfully! 🎉</Typography>
                <Typography variant='h6' fontSize={17} fontWeight='300' textAlign='center' paddingRight={10} paddingLeft={10} marginTop={2}>
                    The financial settlement process was successfully completed in just
                    <strong> {Formatter.timer(timer, 'long')}!</strong>
                </Typography>
                {execEnv.mode !== 'dry-run' && (
                    <Box width='100%' display='flex' justifyContent='center' marginTop={3}>
                        <Button variant='contained' size='large' onClick={execute} sx={{ marginTop: 2 }}> {status === 'loading' && <CircularProgress color='inherit' size={15} sx={{ marginRight: 1 }} />} Go to Settlement details</Button>
                    </Box>
                )}
            </>
        )
    }

    const failedSettlement = () => {
        return (
            <>
                <Box width='100%' display='flex' justifyContent='center'>
                    <Box height={60} width={60} border='2px solid' borderColor={theme.palette.error.main} borderRadius={999} display='flex' justifyContent='center' alignItems='center'>
                        <CloseIcon fontSize='large' color='error' />
                    </Box>
                </Box>
                <Typography variant='h5' textAlign='center' fontWeight='500' marginTop={3}>Oops! Something went wrong 😞</Typography>
                <Typography variant='h6' fontSize={17} fontWeight='300' textAlign='center' paddingRight={10} paddingLeft={10} marginTop={2}>
                    An error occurred during the settlement process. Please correct the issue and rerun the engine.
                </Typography>
            </>
        )
    }

    const completeGeneric = () => {
        return (
            <>
                <Box width='100%' display='flex' justifyContent='center'>
                    <Box height={60} width={60} border='2px solid' borderColor={theme.palette.success.main} borderRadius={999} display='flex' justifyContent='center' alignItems='center'>
                        <CheckIcon fontSize='large' color='success' />
                    </Box>
                </Box>
                <Typography variant='h5' textAlign='center' fontWeight='500' marginTop={3}>Flow Completed Successfully! 🎉</Typography>
                <Typography variant='h6' fontSize={17} fontWeight='300' textAlign='center' paddingRight={10} paddingLeft={10} marginTop={2}>
                    The flow was successfully completed in just
                    <strong> {Formatter.timer(timer, 'long')}!</strong>
                </Typography>
            </>
        )
    }

    const failedGeneric = () => {
        return (
            <>
                <Box width='100%' display='flex' justifyContent='center'>
                    <Box height={60} width={60} border='2px solid' borderColor={theme.palette.error.main} borderRadius={999} display='flex' justifyContent='center' alignItems='center'>
                        <CloseIcon fontSize='large' color='error' />
                    </Box>
                </Box>
                <Typography variant='h5' textAlign='center' fontWeight='500' marginTop={3}>Oops! Something went wrong 😞</Typography>
                <Typography variant='h6' fontSize={17} fontWeight='300' textAlign='center' paddingRight={10} paddingLeft={10} marginTop={2}>
                    An error occurred during the flow process. Please correct the issue and rerun the engine.
                </Typography>
            </>
        )
    }

    return (
        <Box margin='80px auto auto' width={700} sx={{ padding: 'calc(var(--unit) * 2)' }}>
            <Card>
                <CardContent>
                    <Box padding={3}>
                        {renderContent()}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export default ModalEndExecutionEnvironment
